// This service provides functions to interact with the authentication API

import { safeParseJSON } from '../utils/api-utils';
import { getApiBaseUrl } from '../utils/api-base-url';

const API_URL = getApiBaseUrl(); // Configured (prod) or derived from the current host (dev/LAN)

interface SignInCredentials {
  email: string;
  password: string;
}

interface SignUpCredentials {
  email: string;
  password: string;
  password_confirmation: string;
}

interface AuthResponse {
  token: string;
  refresh_token: string;
  user: {
    id: string;
    email: string;
    created_at: string;
    updated_at: string;
  };
}

interface ApiErrorResponse {
  message?: string;
  error?: string;
}

/**
 * Sign in with email and password
 */
export const signIn = async (credentials: SignInCredentials): Promise<AuthResponse> => {
  const response = await fetch(`${API_URL}/api/users/tokens/sign_in`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const error = await safeParseJSON(response) as ApiErrorResponse;
    throw new Error(error.message || 'Failed to sign in');
  }

  return safeParseJSON(response) as Promise<AuthResponse>;
};

/**
 * Sign up with email and password
 */
export const signUp = async (credentials: SignUpCredentials): Promise<AuthResponse> => {
  const response = await fetch(`${API_URL}/api/users/tokens/sign_up`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const error = await safeParseJSON(response) as ApiErrorResponse;
    throw new Error(error.message || 'Failed to sign up');
  }

  return safeParseJSON(response) as Promise<AuthResponse>;
};

/**
 * Sign out (revoke token)
 */
export const signOut = async (token: string): Promise<void> => {
  const response = await fetch(`${API_URL}/api/users/tokens/revoke`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await safeParseJSON(response) as ApiErrorResponse;
    throw new Error(error.message || 'Failed to sign out');
  }
};

/**
 * Refresh the authentication token
 */
export const refreshToken = async (refreshToken: string): Promise<{ token: string }> => {
  const response = await fetch(`${API_URL}/api/users/tokens/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${refreshToken}`, // Use refresh token as Bearer header
      'X-Skip-Service-Worker': 'true', // Bypass service worker for critical auth requests
    },
  });

  if (!response.ok) {
    const error = await safeParseJSON(response) as ApiErrorResponse;
    throw new Error(error.message || 'Failed to refresh token');
  }

  return safeParseJSON(response) as Promise<{ token: string }>;
};

/**
 * Get user information
 */
export const getUserInfo = async (token: string): Promise<{ user: AuthResponse['user'] }> => {
  const response = await fetch(`${API_URL}/api/users/tokens/info`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await safeParseJSON(response) as ApiErrorResponse;
    throw new Error(error.message || 'Failed to get user info');
  }

  return safeParseJSON(response) as Promise<{ user: AuthResponse['user'] }>;
};
